import { useScrollLock } from "@/hooks/useScrollLock";
import type {
  MediaArticleItem,
  MediaArticleParams,
} from "@/services/api/mediaArticle";
import { mediaArticleApi } from "@/services/api/mediaArticle";
import { Drawer, Grid, Pagination, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface MediaArticleDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  domainId?: string;
  brandId?: string;
  categoryId?: string;
  modelId?: string;
  days?: number;
}

const MediaArticleDrawer: React.FC<MediaArticleDrawerProps> = ({
  open,
  onClose,
  title,
  domainId,
  brandId,
  categoryId,
  modelId,
  days,
}) => {
  const { t } = useTranslation();
  const effectiveTitle = title || t("common.modals.media_drawer_title");
  const [articles, setArticles] = useState<MediaArticleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10); // 固定每页10条数据
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // 防止移动端滚动穿透
  useScrollLock(open);

  // 加载数据
  const loadData = async (page: number = 1) => {
    console.log(
      "MediaArticleDrawer: loadData called with page:",
      page,
      "and params:",
      { domainId, brandId, categoryId, modelId, days }
    );
    setLoading(true);
    try {
      // 构造请求参数
      const params: MediaArticleParams = {
        domainId,
        brandId,
        categoryId,
        modelId,
        days,
        page,
        pageSize,
      };

      // 过滤掉undefined的参数
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== undefined)
      ) as MediaArticleParams;

      // 调用API获取数据
      const response = await mediaArticleApi.getMediaArticles(filteredParams);
      console.log("MediaArticleDrawer: API response received:", response);

      setArticles(response.data.list);
      setTotal(response.data.total);
      setCurrentPage(response.data.page);
    } catch (error) {
      console.error("加载文章列表失败:", error);
    } finally {
      setLoading(false);
    }
  };

  // 当抽屉打开时加载数据
  useEffect(() => {
    if (open && domainId) {
      console.log("MediaArticleDrawer: Loading data for domainId:", domainId);
      loadData(1);
    }
  }, [open, domainId, brandId, categoryId, modelId, days]);

  // 处理分页变化
  const handlePageChange = (page: number) => {
    loadData(page);
  };

  return (
    <Drawer
      title={effectiveTitle}
      placement="right"
      onClose={onClose}
      open={open}
      width={isMobile ? "100%" : "60%"}
      className="media-article-drawer"
      maskClosable={isMobile ? false : true} // 禁用点击外部关闭
    >
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: 0,
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <>
            {/* 表头 */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "12px 16px",
                backgroundColor: "#f5f5f5",
                borderBottom: "1px solid #e8e8e8",
                fontWeight: "bold",
              }}
            >
              <div style={{ width: "70%" }}>
                {t("common.charts.citation_content_title_header")}
              </div>
              <div style={{ width: "30%", textAlign: "right" }}>
                {t("common.charts.citation_ratio_header")}
              </div>
            </div>

            <div style={{ flex: 1, overflow: "auto" }}>
              {articles.map((article, index) => (
                <div
                  key={index}
                  style={{
                    padding: isMobile ? "16px" : "16px 30px",
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      maxWidth: "70%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#21C4B8",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      {article.title}
                    </a>
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                    }}
                  >
                    {article.rate}%
                  </div>
                </div>
              ))}
            </div>

            {total > 0 && (
              <div
                style={{
                  padding: "16px 20px",
                  borderTop: "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ fontSize: "14px" }}>共 {total} 条</div>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={total}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  simple
                />
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default MediaArticleDrawer;
